import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Target,
  RefreshCw,
  Table as TableIcon,
  CalendarCheck,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Clock,
  RotateCcw,
} from 'lucide-react';
import { biAPI } from '../../services/api';
import { useBiVendorId } from './useBiVendorId';
import BiBudgetTargetCard from './BiBudgetTargetCard';
import { formatNpr, formatNumber, formatPct } from './biTheme';

const BiBudgetDashboard = () => {
  const { vendorId } = useBiVendorId();
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [budgetMetric, setBudgetMetric] = useState('gmv');
  const [showDetailedBudgetTable, setShowDetailedBudgetTable] = useState(true);

  const queryParams = useMemo(() => ({
    ...(vendorId ? { vendorId } : {}),
    ...(selectedPeriod ? { bsPeriod: selectedPeriod } : {}),
    section: 'core',
  }), [vendorId, selectedPeriod]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery(
    ['bi-budget-dashboard', queryParams],
    async () => {
      const response = await biAPI.getOverview(queryParams);
      return response.data?.data || response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const budgetTargets = data?.budgetTargets || null;

  const currentMonthPeriod = useMemo(() => {
    const curr = budgetTargets?.months?.find((m) => m.isCurrent);
    return curr?.bsPeriod || '';
  }, [budgetTargets?.months]);

  const isSelectedPeriodCurrent = !selectedPeriod || selectedPeriod === currentMonthPeriod;

  const mtdData = useMemo(() => {
    if (!budgetTargets?.mtd) return null;
    if (budgetMetric === 'gmv') return budgetTargets.mtd.gmv;
    if (budgetMetric === 'orders') return budgetTargets.mtd.orders;
    if (budgetMetric === 'ado') return budgetTargets.mtd.ado;
    if (budgetMetric === 'aov') return budgetTargets.mtd.aov;
    return budgetTargets.mtd.gmv;
  }, [budgetTargets, budgetMetric]);

  const qtdData = useMemo(() => {
    if (!budgetTargets?.qtd) return null;
    if (budgetMetric === 'gmv') return budgetTargets.qtd.gmv;
    if (budgetMetric === 'orders') return budgetTargets.qtd.orders;
    if (budgetMetric === 'ado') return budgetTargets.qtd.ado;
    if (budgetMetric === 'aov') return budgetTargets.qtd.aov;
    return budgetTargets.qtd.gmv;
  }, [budgetTargets, budgetMetric]);

  const ytdData = useMemo(() => {
    if (!budgetTargets?.ytd) return null;
    if (budgetMetric === 'gmv') return budgetTargets.ytd.gmv;
    if (budgetMetric === 'orders') return budgetTargets.ytd.orders;
    if (budgetMetric === 'ado') return budgetTargets.ytd.ado;
    if (budgetMetric === 'aov') return budgetTargets.ytd.aov;
    return budgetTargets.ytd.gmv;
  }, [budgetTargets, budgetMetric]);

  return (
    <div className="space-y-4">
      {/* Header Banner */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_6px_20px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-tr from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-500/20">
            <Target className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900">Budget Target vs Achieved & Run Rate</h2>
              {budgetTargets ? (
                <span className="rounded-md bg-purple-50 px-2.5 py-0.5 text-xs font-bold text-purple-700">
                  FY {budgetTargets.fiscalYear} BS
                </span>
              ) : null}
            </div>
            <p className="text-xs text-slate-500">
              {budgetTargets
                ? `Inspecting ${budgetTargets.selectedMonth} (${budgetTargets.selectedQuarter}) & YTD pacing with run-rate projections (Completed Orders only).`
                : 'Pacing and performance tracking against approved annual budget targets.'}
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Month Selector Filter */}
          {budgetTargets?.months?.length ? (
            <div className="flex items-center gap-1.5 rounded-xl border border-purple-200 bg-purple-50/50 px-3 py-1 text-xs font-semibold shadow-sm">
              <Calendar className="h-3.5 w-3.5 text-purple-700" />
              <span className="text-purple-900 font-medium">Month:</span>
              <select
                value={selectedPeriod || budgetTargets.selectedPeriod || ''}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="cursor-pointer bg-transparent font-bold text-purple-950 outline-none hover:text-purple-800"
              >
                {budgetTargets.months.map((m) => (
                  <option key={m.bsPeriod} value={m.bsPeriod}>
                    {m.monthName} ({m.quarter}){m.isCurrent ? ' • Current MTD' : ''}
                  </option>
                ))}
              </select>
              {!isSelectedPeriodCurrent ? (
                <button
                  type="button"
                  onClick={() => setSelectedPeriod('')}
                  title="Reset to current month"
                  className="ml-1 inline-flex items-center gap-0.5 rounded bg-purple-200/70 px-1.5 py-0.5 text-[10px] font-bold text-purple-800 hover:bg-purple-300"
                >
                  <RotateCcw className="h-2.5 w-2.5" /> Current
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Metric Selector */}
          <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1 text-xs font-semibold">
            {[
              { id: 'gmv', label: 'GMV' },
              { id: 'orders', label: 'Orders' },
              { id: 'ado', label: 'ADO (Daily)' },
              { id: 'aov', label: 'AOV' },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setBudgetMetric(m.id)}
                className={`rounded-lg px-3 py-1.5 transition ${
                  budgetMetric === m.id
                    ? 'bg-white text-purple-700 shadow-sm font-bold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setShowDetailedBudgetTable(!showDetailedBudgetTable)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <TableIcon className="h-3.5 w-3.5 text-slate-500" />
            {showDetailedBudgetTable ? 'Hide 12-Month Table' : 'Show 12-Month Table'}
          </button>

          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 text-slate-500 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center text-sm font-medium text-slate-500 shadow-sm">
          Loading budget targets and pacing analysis…
        </div>
      ) : isError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          Failed to load budget data: {error?.response?.data?.message || error?.message || 'Unknown error'}
        </div>
      ) : budgetTargets ? (
        <>
          {/* Executive Target Cards (MTD, QTD, YTD) */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <BiBudgetTargetCard
              title="MTD"
              periodName={budgetTargets.selectedMonth}
              daysLabel={`Day ${budgetTargets.mtd.elapsedDays} of ${budgetTargets.mtd.totalDays} (${budgetTargets.mtd.remainingDays} days left)`}
              metricType={budgetMetric}
              data={mtdData}
              color="#8b5cf6"
            />
            <BiBudgetTargetCard
              title="QTD"
              periodName={`${budgetTargets.selectedQuarter} (Quarter to Date)`}
              daysLabel={`Day ${budgetTargets.qtd.elapsedDays} of ${budgetTargets.qtd.totalDays} (${budgetTargets.qtd.remainingDays} days left)`}
              metricType={budgetMetric}
              data={qtdData}
              color="#3b82f6"
            />
            <BiBudgetTargetCard
              title="YTD"
              periodName={`FY ${budgetTargets.fiscalYear} (Year to Date)`}
              daysLabel={`Day ${budgetTargets.ytd.elapsedDays} of ${budgetTargets.ytd.totalDays} (${budgetTargets.ytd.remainingDays} days left)`}
              metricType={budgetMetric}
              data={ytdData}
              color="#10b981"
            />
          </div>

          {/* Detailed 12-Month & 4-Quarter Table */}
          {showDetailedBudgetTable && budgetTargets.months?.length ? (
            <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_6px_20px_rgba(15,23,42,0.04)]">
              <div className="border-b border-slate-100 bg-slate-50/70 px-4 py-3">
                <h3 className="text-sm font-bold text-slate-900">12-Month Fiscal Budget Breakdown & Run Rates</h3>
                <p className="text-xs text-slate-500">Monthly targets, actual completed performance, and quarter rollup</p>
              </div>

              <div className="max-h-[520px] overflow-auto">
                <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                  <thead className="sticky top-0 z-10 bg-slate-100/95 font-bold uppercase tracking-wider text-slate-700 shadow-sm backdrop-blur">
                    <tr>
                      <th className="px-3.5 py-3">Month / Period</th>
                      <th className="px-2.5 py-3 text-center">Quarter</th>
                      <th className="px-3.5 py-3 text-right">Target GMV</th>
                      <th className="px-3.5 py-3 text-right">Achieved GMV</th>
                      <th className="px-3.5 py-3 text-right">GMV Achieved %</th>
                      <th className="px-3.5 py-3 text-right">Daily Run Rate</th>
                      <th className="px-3.5 py-3 text-right">Run Rate Achieved %</th>
                      <th className="px-3.5 py-3 text-right">Target Orders</th>
                      <th className="px-3.5 py-3 text-right">Achieved Orders</th>
                      <th className="px-3.5 py-3 text-right">Target ADO</th>
                      <th className="px-3.5 py-3 text-right">Target AOV</th>
                      <th className="px-3.5 py-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {budgetTargets.months.map((m) => {
                      const isInspected = m.bsPeriod === (selectedPeriod || budgetTargets.selectedPeriod);
                      return (
                        <tr
                          key={m.bsPeriod}
                          onClick={() => setSelectedPeriod(m.bsPeriod)}
                          title={`Click to inspect ${m.monthName}`}
                          className={`cursor-pointer transition-colors hover:bg-purple-50/70 ${
                            isInspected
                              ? 'bg-purple-100/60 font-semibold ring-1 ring-inset ring-purple-300'
                              : m.isCurrent
                              ? 'bg-purple-50/30'
                              : ''
                          }`}
                        >
                          <td className="px-3.5 py-2.5 font-medium text-slate-800 whitespace-nowrap">
                            {m.monthName}
                            {m.isCurrent ? (
                              <span className="ml-2 inline-flex items-center gap-1 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700">
                                <CalendarCheck className="h-3 w-3" /> Current MTD
                              </span>
                            ) : null}
                          </td>
                          <td className="px-2.5 py-2.5 text-center font-semibold text-slate-500">{m.quarter}</td>
                          <td className="px-3.5 py-2.5 text-right tabular-nums text-slate-700">{formatNpr(m.targetGmv)}</td>
                          <td className="px-3.5 py-2.5 text-right tabular-nums font-bold text-slate-900">{formatNpr(m.achievedGmv)}</td>
                          <td className="px-3.5 py-2.5 text-right tabular-nums font-semibold text-emerald-600">
                            {formatPct(m.gmvAchievedPct, 1)}
                          </td>
                          <td className="px-3.5 py-2.5 text-right tabular-nums text-slate-600">
                            {formatNpr(m.gmvDailyRunRate)} / day
                          </td>
                          <td className="px-3.5 py-2.5 text-right tabular-nums font-extrabold text-purple-700">
                            {m.isFuture ? '—' : formatPct(m.gmvRunRateAchievedPct, 1)}
                          </td>
                          <td className="px-3.5 py-2.5 text-right tabular-nums text-slate-700">{formatNumber(m.targetOrders)}</td>
                          <td className="px-3.5 py-2.5 text-right tabular-nums font-bold text-slate-900">{formatNumber(m.achievedOrders)}</td>
                          <td className="px-3.5 py-2.5 text-right tabular-nums text-slate-600">{formatNumber(m.targetAdo)}</td>
                          <td className="px-3.5 py-2.5 text-right tabular-nums text-slate-600">{formatNpr(m.targetAov)}</td>
                          <td className="px-3.5 py-2.5 text-center whitespace-nowrap">
                            {m.isFuture ? (
                              <span className="text-[11px] text-slate-400">Upcoming</span>
                            ) : m.gmvStatus === 'ahead' ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                                <CheckCircle2 className="h-2.5 w-2.5" /> Ahead
                              </span>
                            ) : m.gmvStatus === 'on_track' ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-800">
                                <Clock className="h-2.5 w-2.5" /> On Track
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                                <AlertCircle className="h-2.5 w-2.5" /> Behind
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {/* Quarters Summary Footers */}
                  <tfoot className="border-t-2 border-slate-300 bg-slate-100 font-bold text-slate-900">
                    {budgetTargets.quarters?.map((q) => (
                      <tr key={q.quarter} className="border-b border-slate-200">
                        <td className="px-3.5 py-2.5 text-slate-800 uppercase tracking-wider">{q.quarter} Total</td>
                        <td className="px-2.5 py-2.5 text-center text-slate-600">{q.quarter}</td>
                        <td className="px-3.5 py-2.5 text-right tabular-nums">{formatNpr(q.targetGmv)}</td>
                        <td className="px-3.5 py-2.5 text-right tabular-nums font-black text-slate-900">{formatNpr(q.achievedGmv)}</td>
                        <td className="px-3.5 py-2.5 text-right tabular-nums text-emerald-700">{formatPct(q.gmvAchievedPct, 1)}</td>
                        <td className="px-3.5 py-2.5 text-right tabular-nums">{formatNpr(q.gmvDailyRunRate)}/d</td>
                        <td className="px-3.5 py-2.5 text-right tabular-nums text-purple-800">{formatPct(q.gmvRunRateAchievedPct, 1)}</td>
                        <td className="px-3.5 py-2.5 text-right tabular-nums">{formatNumber(q.targetOrders)}</td>
                        <td className="px-3.5 py-2.5 text-right tabular-nums font-black">{formatNumber(q.achievedOrders)}</td>
                        <td className="px-3.5 py-2.5 text-right tabular-nums">{formatNumber(q.targetAdo)}</td>
                        <td className="px-3.5 py-2.5 text-right tabular-nums">{formatNpr(q.targetAov)}</td>
                        <td className="px-3.5 py-2.5 text-center">
                          {q.gmvStatus === 'ahead' ? (
                            <span className="inline-flex rounded-full bg-emerald-200 px-2 py-0.5 text-[10px] font-bold text-emerald-900">Ahead</span>
                          ) : q.gmvStatus === 'on_track' ? (
                            <span className="inline-flex rounded-full bg-sky-200 px-2 py-0.5 text-[10px] font-bold text-sky-900">On Track</span>
                          ) : (
                            <span className="inline-flex rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-900">Behind</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tfoot>
                </table>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        /* Empty / Unavailable State */
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
            <Target className="h-6 w-6" />
          </div>
          <h3 className="text-base font-bold text-slate-800">Budget Targets Unavailable</h3>
          <p className="mx-auto max-w-lg text-xs text-slate-600">
            Budget targets have not yet been imported or calendar coverage is missing for the current fiscal year.
            Run the budget import script to configure targets and enable live pacing tracking.
          </p>
        </div>
      )}
    </div>
  );
};

export default BiBudgetDashboard;
